import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { appointmentService } from '../services/AppointmentService';
import { clientService } from '../services/ClientService';
import { userService } from '../services/UserService';
import { sessionService } from '../services/SessionService';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Appointment } from '../models/Appointment';
import { Client } from '../models/Client';
import { User } from '../models/User';
import {
  Calendar,
  Clock,
  MapPin,
  User as UserIcon,
  Video,
  FileText,
  Eye,
  ChevronRight,
  Play,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { format, isToday, isTomorrow, startOfDay } from 'date-fns';

export const SupervisorDashboard: React.FC = () => {
  const { user } = useAuth();
  const { company } = useCompany();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [rbts, setRbts] = useState<User[]>([]);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [activeStreamCount, setActiveStreamCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, company]);

  // Live "Live Now" count — driven by the SAME source the Live Streams page uses
  // (streamSessions for this supervisor, waiting or live), updated in real time.
  // The appointment status was never a reliable signal (it stays 'scheduled').
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'streamSessions'),
      where('supervisorId', '==', user.id),
      where('status', 'in', ['waiting', 'live'])
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => setActiveStreamCount(snapshot.size),
      (err) => {
        console.error('Live stream subscription error:', err);
        setActiveStreamCount(0);
      }
    );
    return () => unsubscribe();
  }, [user]);

  const loadData = async () => {
    if (!user || !company) {
      setLoading(false);
      return;
    }

    try {
      const [userAppointments, companyClients, companyUsers] = await Promise.all([
        appointmentService.getAppointmentsBySupervisor(user.id),
        clientService.getClientsByCompany(company.id),
        userService.getUsersByCompany(company.id),
      ]);

      // Filter to upcoming appointments only (exclude completed/cancelled —
      // they're no longer "upcoming").
      const today = startOfDay(new Date());
      const upcoming = userAppointments.filter(
        apt =>
          apt.startTime >= today &&
          apt.status !== 'completed' &&
          apt.status !== 'cancelled'
      );

      setAppointments(upcoming);
      setClients(companyClients);
      setRbts(companyUsers.filter(u => u.role === 'rbt'));

      // Pending review count — read the SAME source as the BCBA Review page
      // (companies/sessions submitted/revised), so the count and the page agree.
      try {
        const pendingSessions = await sessionService.getPendingReviewSessions(user.companyId);
        setPendingReviewCount(pendingSessions.length);
      } catch {
        // Query might fail (e.g. permissions) — degrade to 0 rather than break.
        setPendingReviewCount(0);
      }
    } catch (error) {
      console.error('Error loading supervisor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.firstName} ${client.lastName}` : 'Unknown Client';
  };

  const getRbtName = (rbtId: string) => {
    const rbt = rbts.find(r => r.id === rbtId);
    return rbt?.name || 'Unknown RBT';
  };

  const formatAppointmentDate = (date: Date) => {
    if (isToday(date)) {
      return 'Today';
    } else if (isTomorrow(date)) {
      return 'Tomorrow';
    } else {
      return format(date, 'EEE, MMM d');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return '#6b6b6b';
      case 'checked-in': return '#f59e0b';
      case 'in-progress': return '#10b981';
      case 'completed': return '#3d4d50';
      case 'cancelled': return '#ef4444';
      default: return '#6b6b6b';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'in-progress': return <Play size={14} />;
      case 'completed': return <CheckCircle size={14} />;
      case 'cancelled': return <AlertCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  // Get today's appointments count
  const todayCount = appointments.filter(apt => isToday(apt.startTime)).length;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <div
          style={{
            display: 'inline-block',
            width: '48px',
            height: '48px',
            border: '4px solid #e5e0db',
            borderTop: '4px solid #4F6367',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ marginTop: '16px', color: '#6b6b6b' }}>Loading dashboard...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ marginBottom: '8px', fontSize: '28px', fontWeight: 700, color: '#1f2937' }}>
          Welcome back, {user?.name}!
        </h1>
        <p style={{ color: '#6b6b6b', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {user?.email}
          <span
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            SUPERVISOR
          </span>
        </p>
      </div>

      {/* Quick Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/appointments')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: '#dbeafe',
              padding: '12px',
              borderRadius: '10px'
            }}>
              <Calendar size={24} style={{ color: '#3b82f6' }} />
            </div>
            <div>
              <p style={{ fontSize: '14px', color: '#6b6b6b' }}>Today's Sessions</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937' }}>{todayCount}</p>
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/live-streams')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: '#d1fae5',
              padding: '12px',
              borderRadius: '10px'
            }}>
              <Video size={24} style={{ color: '#10b981' }} />
            </div>
            <div>
              <p style={{ fontSize: '14px', color: '#6b6b6b' }}>Live Now</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937' }}>{activeStreamCount}</p>
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/review-notes')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: pendingReviewCount > 0 ? '#fef3c7' : '#f3f4f6',
              padding: '12px',
              borderRadius: '10px'
            }}>
              <FileText size={24} style={{ color: pendingReviewCount > 0 ? '#f59e0b' : '#6b6b6b' }} />
            </div>
            <div>
              <p style={{ fontSize: '14px', color: '#6b6b6b' }}>Pending Reviews</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: pendingReviewCount > 0 ? '#f59e0b' : '#1f2937' }}>
                {pendingReviewCount}
              </p>
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/calendar')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: '#e0e7ff',
              padding: '12px',
              borderRadius: '10px'
            }}>
              <Eye size={24} style={{ color: '#6366f1' }} />
            </div>
            <div>
              <p style={{ fontSize: '14px', color: '#6b6b6b' }}>Total Upcoming</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937' }}>{appointments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Appointments List */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '32px',
        }}
      >
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e5e0db',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
            Upcoming Appointments
          </h2>
          <button
            onClick={() => navigate('/calendar')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: '#3b82f6',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            View Calendar <ChevronRight size={16} />
          </button>
        </div>

        {appointments.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <Calendar size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
            <p style={{ color: '#6b6b6b' }}>No upcoming appointments</p>
            <button
              onClick={() => navigate('/appointments')}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Schedule Appointment
            </button>
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {appointments.slice(0, 10).map((apt, index) => (
              <div
                key={apt.id}
                style={{
                  padding: '16px 20px',
                  borderBottom: index < Math.min(appointments.length - 1, 9) ? '1px solid #f3f4f6' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onClick={() => navigate('/appointments')}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Date badge */}
                  <div style={{
                    backgroundColor: isToday(apt.startTime) ? '#dbeafe' : '#f3f4f6',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    minWidth: '80px',
                    textAlign: 'center',
                  }}>
                    <p style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: isToday(apt.startTime) ? '#3b82f6' : '#6b6b6b'
                    }}>
                      {formatAppointmentDate(apt.startTime)}
                    </p>
                    <p style={{ fontSize: '14px', fontWeight: 700, color: '#1f2937' }}>
                      {format(apt.startTime, 'h:mm a')}
                    </p>
                  </div>

                  {/* Appointment details */}
                  <div>
                    <p style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                      {getClientName(apt.clientId)}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#6b6b6b' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <UserIcon size={14} />
                        {getRbtName(apt.rbtId)}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={14} />
                        {apt.location.city}, {apt.location.state}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={14} />
                        {apt.duration} min
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status and actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {apt.cameraRequired && (
                    <div style={{
                      backgroundColor: '#d1fae5',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}>
                      <Video size={14} style={{ color: '#10b981' }} />
                      <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 500 }}>Camera</span>
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: `${getStatusColor(apt.status)}20`,
                    color: getStatusColor(apt.status),
                    fontSize: '12px',
                    fontWeight: 500,
                  }}>
                    {getStatusIcon(apt.status)}
                    {apt.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                  <ChevronRight size={20} style={{ color: '#d1d5db' }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {appointments.length > 10 && (
          <div style={{
            padding: '12px 20px',
            borderTop: '1px solid #e5e0db',
            textAlign: 'center',
          }}>
            <button
              onClick={() => navigate('/appointments')}
              style={{
                color: '#3b82f6',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              View all {appointments.length} appointments
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate('/live-streams')}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'box-shadow 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
        >
          <Video size={24} style={{ color: '#10b981', marginBottom: '12px' }} />
          <p style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>Live Streams</p>
          <p style={{ fontSize: '13px', color: '#6b6b6b' }}>Watch active RBT sessions</p>
        </button>

        <button
          onClick={() => navigate('/review-notes')}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'box-shadow 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
        >
          <FileText size={24} style={{ color: '#f59e0b', marginBottom: '12px' }} />
          <p style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>Review Notes</p>
          <p style={{ fontSize: '13px', color: '#6b6b6b' }}>Review and approve session notes</p>
        </button>

        <button
          onClick={() => navigate('/location-audit')}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'box-shadow 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
        >
          <MapPin size={24} style={{ color: '#6366f1', marginBottom: '12px' }} />
          <p style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>Location Audit</p>
          <p style={{ fontSize: '13px', color: '#6b6b6b' }}>Verify RBT check-in locations</p>
        </button>
      </div>
    </div>
  );
};
